import { HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { REFERER, SENTRY, SentryContextKeys } from '@core/const';
import { ErrorModel } from '@spartacus/core';
import { GetMetricsEndpointService } from '../../services';

type SentryContext = Partial<Record<SentryContextKeys, any>>;

interface CustomError extends Error {
  tags?: SentryContext;
  extras?: SentryContext;
}

@Injectable({
  providedIn: 'root',
})
export class SentryErrorHandler implements ErrorHandler {
  private readonly getMetricsEndpointService = inject(GetMetricsEndpointService);
  private readonly referer = inject(REFERER);
  private readonly sentry = inject(SENTRY);

  handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);

      return;
    }

    this.sentry.captureUnhandledException(error);
  }

  handleHttpError(response: HttpErrorResponse, request?: HttpRequest<any>): void {
    const error = this.createErrorFromHttpErrorResponse(response, request);

    this.sentry.withScope((scope) => {
      if (error.tags) {
        scope.setTags(error.tags);
      }

      if (error.extras) {
        scope.setExtras(error.extras);
      }

      this.sentry.captureException(error);
    });
  }

  private createErrorFromHttpErrorResponse(response: HttpErrorResponse, request?: HttpRequest<any>): CustomError {
    const responseError: ErrorModel = response.error?.errors?.[0] || {};
    const error = new Error() as CustomError;
    delete error.stack;

    error.message = responseError.message ?? response.message;
    error.name = responseError.type ?? response.name;

    error.tags = {
      ...(request && {
        [SentryContextKeys.RequestMethod]: request.method,
        [SentryContextKeys.RequestMask]: this.getMetricsEndpointService.getEndpoint(request),
      }),
      [SentryContextKeys.ResponseStatus]: response.status,
    };

    error.extras = {
      ...(this.referer && {
        [SentryContextKeys.Referer]: this.referer,
      }),
      ...(request && {
        [SentryContextKeys.RequestMethod]: request.method,
        [SentryContextKeys.RequestUrlWithParams]: request.urlWithParams,
        [SentryContextKeys.RequestUrl]: request.url,
        [SentryContextKeys.RequestParams]: request.params.toString(),
        [SentryContextKeys.RequestHeaders]: this.serializeHeaders(request.headers),
        [SentryContextKeys.RequestBody]: this.serializeBody(request.body),
      }),
      [SentryContextKeys.ResponseUrl]: response.url,
      [SentryContextKeys.ResponseHeaders]: this.serializeHeaders(response.headers),
      [SentryContextKeys.ResponseOk]: response.ok,
      [SentryContextKeys.ResponseStatus]: response.status,
      [SentryContextKeys.ResponseStatusText]: response.statusText,
      [SentryContextKeys.ResponseMessage]: response.message,
      [SentryContextKeys.ResponseError]: this.serializeBody(response.error),
      ...(responseError.message && {
        [SentryContextKeys.ResponseErrorMessage]: responseError.message,
      }),
      ...(responseError.reason && {
        [SentryContextKeys.ResponseErrorReason]: responseError.reason,
      }),
      ...(responseError.subject && {
        [SentryContextKeys.ResponseErrorSubject]: responseError.subject,
      }),
      ...(responseError.subjectType && {
        [SentryContextKeys.ResponseErrorSubjectType]: responseError.subjectType,
      }),
      ...(responseError.type && {
        [SentryContextKeys.ResponseErrorType]: responseError.type,
      }),
    };

    return error;
  }

  private safeStringify(value: unknown): string {
    const seen = new WeakSet<object>();

    try {
      return (
        JSON.stringify(value, (_k, v) => {
          if (typeof v === 'object' && v !== null) {
            if (seen.has(v)) return '[Circular]';
            seen.add(v);
          }

          return v;
        }) ?? ''
      );
    } catch {
      return String(value);
    }
  }

  private serializeHeaders(headers?: HttpHeaders | null): Record<string, string> {
    if (!headers) return {};

    const excluded = ['Authorization'];

    const out: Record<string, string> = {};
    for (const key of headers.keys()) {
      if (!excluded.includes(key)) {
        const value = headers.get(key);
        if (value !== null) out[key] = value;
      }
    }

    return out;
  }

  private serializeBody(body: unknown, maxLen = 1000): string {
    if (!body) return '';

    const s = typeof body === 'string' ? body : this.safeStringify(body);

    return s.length > maxLen ? s.slice(0, maxLen) + '…[truncated]' : s;
  }
}
